import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, ReactNode } from 'react';
import { ADDRESS_CONNECTED_TRANSACTIONS, ADDRESS_LAST_ADDRESS } from '../data/js/server-constants';

/** Module constant: an inline arrow would be a new value on every render. */
const getProductPath = (id: string) => `/product/${id}`;
import { identifyUser, resetUserTracking, trackError, severityForHttpStatus, ErrorSeverity } from '../utils/error-tracking';
import { trackAccountEvent } from '../utils/track-account';
import {
  GetSellerTransactionsResponseSchema,
  type GetSellerTransactionsResponse,
  type SellerTransaction,
} from '@/schemas/api/transactions/seller';
import {
  GetBuyerTransactionsResponseSchema,
  type BuyerTransaction,
} from '@/schemas/api/transactions/buyer';
import {
  GetShippingLabelResponseSchema,
  RetryShippingLabelResponseSchema,
  LabelQuoteResponseSchema,
  ShippingQrCodeResponseSchema,
  type ShippingQrCodeResponse,
  type LabelQuoteResponse,
  type ShippingLabelResult,
} from '@/schemas/api/shipping/label';
import {
  GetAddressesResponseSchema,
  DeactivateAddressResponseSchema,
  type CreateAddressRequest,
  type CreateAddressResponse
} from '@/schemas/api/addresses';
import {
  type MarkTransactionReadResponse,
} from '@/schemas/api/transactions/read';
import { type Address as AddressModel } from '@/schemas/models/address';

export interface Transaction {
  id: string;
  listingId: string;
  seller: {
    id: string;
    read: string;
  };
  buyer: {
    id: string;
    read: string;
  };
}

export interface Address {
  id?: string;
  shippingName: string;
  shippingEmail?: string;
  shippingPhone?: string;
  line1: string;
  line2: string;
  locality: string;
  region: string;
  zipCode: string;
  latitude: string;
  longitude: string;
  lat?: string;
  lng?: string;
  countryId: string;
  countryCode?: { name: string; code: string };
  isDefault: boolean | number;
  country?: string;
  locationText?: string;
}

export interface UserProfile {
  id: string | number;
  firstName?: string;
  lastName?: string;
  [key: string]: unknown;
}

export interface NotificationPayload {
  type: string;
  actionURL?: string;
  dismissed?: boolean;
  msg: string;
}

export interface SessionContextType {
  // Client State
  clientSide: boolean;

  // Transactions
  transactions: Transaction[];

  // Addresses
  addresses: Address[];
  shippingAddress: Address;
  tempShippingAddress: Address;

  // Transaction Actions
  getTransactionsOnLoad: () => Promise<void>;
  getSellerTransactions: (listingId?: number) => Promise<GetSellerTransactionsResponse>;
  getShippingLabel: (shippingId: number | string) => Promise<ShippingLabelResult>;
  retryShippingLabel: (shippingId: number | string, rateId?: string) => Promise<ShippingLabelResult>;
  /** The USPS QR code for a bought label, or null when it has none (utils/usps-qr.ts). */
  getShippingQrCode: (shippingId: number | string) => Promise<ShippingQrCodeResponse>;
  getLabelQuote: (shippingId: number | string) => Promise<LabelQuoteResponse>;
  /**
   * Whether `addresses` has been fetched yet.
   *
   * Without it an empty list is ambiguous — "this seller has no address" and
   * "nobody has asked yet" look identical, and a screen that acts on the first
   * meaning will accuse a seller with three saved addresses of having none.
   */
  addressesLoaded: boolean;
  markTransactionRead: (id: string) => Promise<MarkTransactionReadResponse | null>;
  getUnreadNotices: () => Promise<void>;

  // Address Actions
  getAddresses: (profile?: UserProfile) => Promise<Address[]>;
  createAddress: (address: CreateAddressRequest) => Promise<CreateAddressResponse>;
  setShippingAddress: (address: Address) => void;
  setTempShippingAddress: (address: Address) => void;
  setDefaultAddress: (id: string) => Promise<void>;
  deactivateAddress: (id: string) => Promise<boolean>;
  setLocation: (address: Address) => Promise<void>;
  geoLocate: (address: Address) => Promise<Address>;
  getProductPath?: (id: string) => string;
}

const AppSessionContext = createContext<SessionContextType | undefined>(undefined);

export const useAppSession = () => {
  const context = useContext(AppSessionContext);
  if (!context) {
    throw new Error('useAppSession must be used within an AppSessionProvider');
  }
  return context;
};

interface AppSessionProviderProps {
  children: ReactNode;
  profile?: UserProfile;
  onUnreadCountsUpdate?: (sales: number, purchases: number, transactions: number) => void;
  onNotification?: (notification: NotificationPayload) => void;
  /**
   * Called once an order is marked read. Marking it read also marks its
   * notifications read, and those are what the account rail badges count, so
   * the badge counts need fetching again.
   */
  onTransactionRead?: () => void | Promise<void>;
}

const defaultAddress: Address = {
  shippingName: '',
  line1: '',
  line2: '',
  locality: '',
  region: '',
  zipCode: '',
  latitude: '',
  longitude: '',
  countryId: '',
  isDefault: true,
};

/**
 * trackError's arguments for a provider fault, filed under its action. A non-Error
 * throw is filed as an Error carrying `fallback`, so the row still has a message
 * and a stack. Spread into trackError at the call, where the catch lint looks.
 */
function sessionErrorArgs(
  action: string,
  error: unknown,
  fallback = 'AppSessionProvider error'
): Parameters<typeof trackError> {
  return [
    error instanceof Error ? error : new Error(fallback),
    ErrorSeverity.ERROR,
    { action, component: 'AppSessionProvider' },
  ];
}

/** The sentence a caller is handed for a throw: its own, or `fallback` for a non-Error. */
function thrownMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/** An address id for an account event: a number, or nothing for an id that is not one. */
function addressIdForEvent(id: string): number | undefined {
  return Number(id) || undefined;
}

export const AppSessionProvider: React.FC<AppSessionProviderProps> = ({
  children,
  profile,
  onUnreadCountsUpdate,
  onNotification,
  onTransactionRead,
}) => {
  const [clientSide, setClientSide] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [shippingAddress, setShippingAddressState] = useState<Address>(defaultAddress);
  const [tempShippingAddress, setTempShippingAddressState] = useState<Address>(defaultAddress);
  const addressesFetchingRef = useRef(false); // Prevent concurrent address fetches
  const [addressesLoaded, setAddressesLoaded] = useState(false); // Track if addresses have been loaded
  const tempShippingAddressRef = useRef(tempShippingAddress);

  // Keep ref in sync with the latest tempShippingAddress for use in async callbacks
  useEffect(() => {
    tempShippingAddressRef.current = tempShippingAddress;
  }, [tempShippingAddress]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional post-mount client flag to avoid SSR hydration mismatch; clientSide cannot be derived during render
    setClientSide(true);
  }, []);

  // Identify user for error tracking when profile is available
  useEffect(() => {
    if (profile?.id) {
      // Convert profile ID to number for error tracking
      const userId = typeof profile.id === 'string' ? parseInt(profile.id, 10) : profile.id;
      if (!isNaN(userId)) {
        identifyUser(userId);
      }
    } else {
      // Reset tracking when user logs out
      resetUserTracking();
    }
  }, [profile?.id]);


  /** Tell the app shell, when it asked to be told. */
  const notify = useCallback((notification: NotificationPayload) => {
    if (onNotification) onNotification(notification);
  }, [onNotification]);

  const getTransactionsOnLoad = useCallback(async () => {
    if (!profile?.id) return;

    try {
      // Fetch both seller and buyer transactions
      const [sellerData, buyerData] = await Promise.all([
        fetch('/api/transactions/seller').then(r => r.json()),
        fetch('/api/transactions/buyer').then(r => r.json())
      ]);

      // Validate seller transactions response
      const sellerParseResult = GetSellerTransactionsResponseSchema.safeParse(sellerData);
      if (!sellerParseResult.success) {
        trackError(...sessionErrorArgs('getTransactionsOnLoad', new Error('Seller transactions validation failed')));
      }
      const sellerResponse = sellerParseResult.success ? sellerParseResult.data : sellerData;

      // Validate buyer transactions response
      const buyerParseResult = GetBuyerTransactionsResponseSchema.safeParse(buyerData);
      if (!buyerParseResult.success) {
        trackError(...sessionErrorArgs('getTransactionsOnLoad', new Error('Buyer transactions validation failed')));
      }
      const buyerResponse = buyerParseResult.success ? buyerParseResult.data : buyerData;

      // Combine into a single transaction list (matching old RPC format)
      const transactionList: Transaction[] = [];

      // Add seller transactions
      if (sellerResponse.success && sellerResponse.transactions) {
        sellerResponse.transactions.forEach((tx: SellerTransaction) => {
          transactionList.push({
            id: String(tx.id),
            listingId: String(tx.listingId),
            buyer: {
              id: String(tx.buyer?.id || tx.buyerId),
              read: tx.buyerRead ? '1' : '0',
            },
            seller: {
              id: String(profile.id),
              read: tx.read ? '1' : '0',
            },
          });
        });
      }

      // Add buyer transactions
      if (buyerResponse.success && buyerResponse.transactions) {
        buyerResponse.transactions.forEach((tx: BuyerTransaction) => {
          transactionList.push({
            id: String(tx.id),
            listingId: String(tx.listingId),
            buyer: {
              id: String(profile.id),
              read: tx.buyer?.read ? '1' : '0',
            },
            seller: {
              id: String(tx.seller?.id || tx.sellerId),
              read: tx.read ? '1' : '0',
            },
          });
        });
      }

      const unreadSalesCount = transactionList.reduce((count: number, t: Transaction) => {
        return t.seller.read === '0' && t.seller.id === profile.id ? count + 1 : count;
      }, 0);

      const unreadPurchasesCount = transactionList.reduce((count: number, t: Transaction) => {
        return t.buyer.read === '0' && t.buyer.id === profile.id ? count + 1 : count;
      }, 0);

      setTransactions(transactionList);

      if (onUnreadCountsUpdate) {
        onUnreadCountsUpdate(unreadSalesCount, unreadPurchasesCount, 0);
      }

      // Create notifications for unread transactions
      transactionList.forEach((t: Transaction) => {
        if (t.buyer.id === profile.id && t.buyer.read === '0') {
          notify({
            type: 'action',
            actionURL: `/account/purchases/${t.listingId}`,
            dismissed: true,
            msg: `Purchase update`,
          });
        }
        if (t.seller.id === profile.id && t.seller.read === '0') {
          notify({
            type: 'action',
            actionURL: `/account/sales/${t.listingId}`,
            dismissed: true,
            msg: `Sale update`,
          });
        }
      });
    } catch (error) {
      trackError(...sessionErrorArgs('getTransactionsOnLoad', error, 'Error loading transactions'));
    }
  }, [profile, onUnreadCountsUpdate, notify]);

  const markTransactionRead = useCallback(async (id: string): Promise<MarkTransactionReadResponse | null> => {
    try {
      const response = await fetch(`/api/transactions/${id}/read`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json() as MarkTransactionReadResponse;

      if (data.success) {
        await getTransactionsOnLoad();
        if (onTransactionRead) await onTransactionRead();
      }
      return data;
    } catch (error) {
      trackError(...sessionErrorArgs('markTransactionRead', error, 'Error marking transaction as read'));
      return null;
    }
  }, [getTransactionsOnLoad, onTransactionRead]);

  const getUnreadNotices = useCallback(async () => {
    // Simply reload transactions - the count will be computed automatically
    await getTransactionsOnLoad();
  }, [getTransactionsOnLoad]);

  /**
   * Get seller transactions with full type safety and Zod validation
   * Replaces getSellerTransactionsNextAPI from network.ts
   * @param listingId - Optional listing ID to filter transactions
   */
  const getSellerTransactions = useCallback(async (listingId?: number): Promise<GetSellerTransactionsResponse> => {
    try {
      const url = listingId
        ? `/api/transactions/seller?listingId=${listingId}`
        : '/api/transactions/seller';

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      // Validate response with Zod
      const parseResult = GetSellerTransactionsResponseSchema.safeParse(data);
      if (!parseResult.success) {
        trackError(...sessionErrorArgs('getSellerTransactions', new Error('Seller transactions validation failed')));
        return {
          success: false,
          error: 'Invalid response format from server'
        };
      }

      return parseResult.data;
    } catch (error) {
      trackError(...sessionErrorArgs('getSellerTransactions', error, 'Error fetching seller transactions'));
      return {
        success: false,
        error: thrownMessage(error, 'Failed to fetch seller transactions')
      };
    }
  }, []);

  /**
   * Get shipping label URL for a shipping record
   * Replaces getShippingLabelNextAPI from network.ts
   */
  const getShippingLabel = useCallback(async (shippingId: number | string): Promise<ShippingLabelResult> => {
    try {
      const response = await fetch(`/api/shipping/${shippingId}/label`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      // Validate response with Zod
      const parseResult = GetShippingLabelResponseSchema.safeParse(data);
      if (!parseResult.success) {
        trackError(...sessionErrorArgs('getShippingLabel', new Error('Shipping label validation failed')));
        return { success: false, error: 'Invalid response format from server' };
      }

      return parseResult.data as ShippingLabelResult;
    } catch (error) {
      trackError(...sessionErrorArgs('getShippingLabel', error, 'Error fetching shipping label'));
      return {
        success: false,
        error: thrownMessage(error, 'Failed to fetch shipping label')
      };
    }
  }, []);

  /** "Use QR-Code Instead": the label's USPS QR code, from GET /api/shipping/[id]/qr-code. */
  const getShippingQrCode = useCallback(async (shippingId: number | string): Promise<ShippingQrCodeResponse> => {
    try {
      const response = await fetch(`/api/shipping/${shippingId}/qr-code`);
      const parsed = ShippingQrCodeResponseSchema.safeParse(await response.json());
      if (!parsed.success) return { success: false, error: 'Invalid response format from server' };
      return parsed.data;
    } catch (error) {
      trackError(...sessionErrorArgs('getShippingQrCode', error, 'Error fetching QR code'));
      return { success: false, error: 'Could not load the QR code' };
    }
  }, []);

  /**
   * Buy a shipping label the carrier refused the first time.
   *
   * Called by the Print Label button when the label endpoint reports the
   * shipment is recoverable, so a seller whose label failed at accept-time gets
   * it from the same click instead of having to contact support.
   */
  const retryShippingLabel = useCallback(async (shippingId: number | string, rateId?: string): Promise<ShippingLabelResult> => {
    try {
      const response = await fetch(`/api/shipping/${shippingId}/retry-label`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // A rate the seller chose off the quote. Omitted, the endpoint re-buys
        // the carrier and service the buyer already paid for.
        body: JSON.stringify(rateId ? { rateId } : {}),
      });

      const data = await response.json();

      const parseResult = RetryShippingLabelResponseSchema.safeParse(data);
      if (!parseResult.success) {
        trackError(...sessionErrorArgs('retryShippingLabel', new Error('Retry shipping label validation failed')));
        return { success: false, error: 'Invalid response format from server' };
      }

      const result = parseResult.data;
      if (result.success === false) {
        return { success: false, error: result.error };
      }
      return { success: true, labelUrl: result.labelUrl };
    } catch (error) {
      trackError(...sessionErrorArgs('retryShippingLabel', error, 'Error buying shipping label'));
      return {
        success: false,
        error: thrownMessage(error, 'Failed to buy shipping label')
      };
    }
  }, []);

  /**
   * Rates for an order that was never quoted a carrier.
   *
   * Separate from `retryShippingLabel` because it buys nothing: the seller is
   * being shown what the label costs against what the buyer paid, and decides.
   */
  const getLabelQuote = useCallback(async (shippingId: number | string): Promise<LabelQuoteResponse> => {
    try {
      const response = await fetch(`/api/shipping/${shippingId}/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const parseResult = LabelQuoteResponseSchema.safeParse(await response.json());
      if (!parseResult.success) {
        trackError(...sessionErrorArgs('getLabelQuote', new Error('Label quote validation failed')));
        return { success: false, error: 'Invalid response format from server' };
      }
      return parseResult.data;
    } catch (error) {
      trackError(...sessionErrorArgs('getLabelQuote', error, 'Error getting label rates'));
      return {
        success: false,
        error: thrownMessage(error, 'Failed to get shipping rates'),
      };
    }
  }, []);

  const geoLocate = useCallback(async (address: Address): Promise<Address> => {
    // TEMPORARILY DISABLED: Geocoding is disabled due to Google Maps API key restrictions
    // The API key needs to have dev.sellwild.com added to allowed referrers
    return address;

    /* ORIGINAL CODE - UNCOMMENT WHEN API KEY IS FIXED
    if (typeof window === 'undefined' || !window.google?.maps) {
      // Wait for Google Maps to load
      return new Promise((resolve) => {
        const checkGoogle = setInterval(() => {
          if (window.google?.maps) {
            clearInterval(checkGoogle);
            geoLocate(address).then(resolve);
          }
        }, 500);

        setTimeout(() => {
          clearInterval(checkGoogle);
          resolve(address);
        }, 5000);
      });
    }

    try {
      const geocoder = new google.maps.Geocoder();
      let params: any;

      if (address.latitude && address.longitude) {
        params = {
          location: {
            lat: parseFloat(address.latitude),
            lng: parseFloat(address.longitude),
          },
        };
      } else {
        const addressString = [
          address.line1,
          address.line2,
          address.locality,
          address.region,
          address.zipCode,
          address.country,
        ].filter(Boolean).join(', ');

        params = { address: addressString };
      }

      let response;
      try {
        response = await geocoder.geocode(params);
      } catch (error) {
        console.error('Geocoding error:', error);
        // Return the original address if geocoding fails
        return address;
      }

      if (!response?.results?.[0]) return address;

      const place = response.results[0];
      const updatedAddress = { ...address };

      updatedAddress.latitude = String(place.geometry.location.lat());
      updatedAddress.longitude = String(place.geometry.location.lng());

      for (const component of place.address_components) {
        const type = component.types[0];
        switch (type) {
          case 'street_number':
            updatedAddress.line1 = component.long_name;
            break;
          case 'route':
            updatedAddress.line1 = (updatedAddress.line1 || '') + ' ' + component.long_name;
            break;
          case 'locality':
            updatedAddress.locality = component.long_name;
            break;
          case 'administrative_area_level_1':
            updatedAddress.region = component.short_name;
            break;
          case 'postal_code':
            updatedAddress.zipCode = component.short_name;
            break;
          case 'country':
            updatedAddress.country = component.long_name;
            break;
        }
      }

      return updatedAddress;
    } catch (error) {
      console.error('Error geocoding address:', error);
      return address;
    }
    */
  }, []);

  /**
   * Fill in an address's coordinates. A no-op while geocoding is disabled (see
   * geoLocate): it only ever ran for an address WITHOUT coordinates, and the
   * disabled geoLocate hands that address back unchanged, so there was never
   * anything to set. Restore the lookup here when geoLocate is restored.
   */
  const setLocation = useCallback(async (_address: Address) => {}, []);

  // Internal function that actually fetches addresses from API
  // No in-flight check of its own: getAddresses checks before calling, and every
  // other caller clears the flag first because it means to fetch again.
  const fetchAddressesInternal = useCallback(async (userProfile?: UserProfile) => {
    addressesFetchingRef.current = true;

    const currentProfile = userProfile || profile;
    const fullName = `${currentProfile?.firstName || ''} ${currentProfile?.lastName || ''}`.trim();

    try {
      const apiResponse = await fetch('/api/addresses', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await apiResponse.json();
      const response = GetAddressesResponseSchema.parse(data);

      if (response?.success && response?.addresses?.length) {
        const addressList = response.addresses.reverse();
        const defaultAddress = addressList.find((a: AddressModel) => a.isDefault === 1) || addressList[0];

        const shipping = {
          ...defaultAddress,
          shippingName: defaultAddress.fullName || fullName,
        };

        setAddresses(addressList as unknown as Address[]);
        setShippingAddressState(shipping as unknown as Address);
        setTempShippingAddressState({
          ...tempShippingAddressRef.current,
          shippingName: fullName,
        });

        setAddressesLoaded(true);
        addressesFetchingRef.current = false;
      } else {
        setAddresses([]);
        setTempShippingAddressState({
          ...tempShippingAddressRef.current,
          shippingName: fullName,
        });
        setAddressesLoaded(true);
        addressesFetchingRef.current = false;
      }
    } catch (error) {
      trackError(...sessionErrorArgs('fetchAddressesInternal', error, 'Error fetching addresses'));
      // Settle the flag even on failure. A caller that waits for `addressesLoaded`
      // before deciding what to show would otherwise wait forever on a seller
      // whose fetch failed once, leaving them no way to add an address at all.
      // Failing open shows the add-address form, which is where this was before
      // the flag existed, and the server still rejects a genuine duplicate.
      setAddressesLoaded(true);
      addressesFetchingRef.current = false;
    }
  }, [profile]);

  // Public function that returns cached addresses or triggers a refresh if needed
  const getAddresses = useCallback(async (userProfile?: UserProfile) => {
    // If addresses are already loaded, just return them
    if (addressesLoaded && !userProfile) {
      return addresses;
    }

    // If not loaded yet, trigger a fetch
    if (!addressesFetchingRef.current) {
      await fetchAddressesInternal(userProfile);
    }

    return addresses;
  }, [addresses, addressesLoaded, fetchAddressesInternal]);

  /**
   * Create a new address with full type safety and Zod validation
   * Replaces createAddressNextAPI from network.ts
   */
  const createAddress = useCallback(async (address: CreateAddressRequest): Promise<CreateAddressResponse> => {
    try {
      const response = await fetch('/api/addresses', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(address),
      });

      if (!response.ok) {
        const errorText = await response.text();
        trackError(
          new Error(`HTTP error response: ${errorText}`),
          severityForHttpStatus(response.status),
          { action: 'createAddress', component: 'AppSessionProvider', extra: { status: response.status } }
        );
        return {
          success: false,
          error: `HTTP ${response.status}: ${errorText || response.statusText}`
        };
      }

      const data = await response.json() as CreateAddressResponse;

      // If successful, refresh addresses
      if (data.success) {
        void trackAccountEvent({
          event_type: 'address_added',
          section: 'addresses',
          address_id: typeof data.address?.id === 'number' ? data.address.id : undefined,
        });
        setAddressesLoaded(false);
        addressesFetchingRef.current = false; // Reset flag to allow fetch
        await fetchAddressesInternal();
      }

      return data;
    } catch (error) {
      trackError(...sessionErrorArgs('createAddress', error, 'Error creating address'));
      return {
        success: false,
        error: thrownMessage(error, 'Failed to create address')
      };
    }
  }, [fetchAddressesInternal]);

  // Addresses are now fetched on-demand only (not auto-fetched on app load)
  // Components that need addresses should call getAddresses() explicitly
  // This reduces unnecessary API calls on every page load

  // Listen for address mutations and auto-refresh
  // Effects never run on the server, so `window` is always there.
  useEffect(() => {
    const refreshAddresses = () => {
      setAddressesLoaded(false);
      addressesFetchingRef.current = false; // Reset flag to allow fetch
      void fetchAddressesInternal();
    };

    void import('../utils/dom').then(({ receive }) => {
      receive('afterAddressAdded', refreshAddresses);
      receive('afterAddressUpdated', refreshAddresses);
      receive('afterAddressDeleted', refreshAddresses);
    });

    // No cleanup needed - receive() doesn't return unsubscribe function
  }, [fetchAddressesInternal]); // Re-registers listeners when fetchAddressesInternal changes

  const setShippingAddress = useCallback((address: Address) => {
    setShippingAddressState(address);
  }, []);

  const setTempShippingAddress = useCallback((address: Address) => {
    setTempShippingAddressState(address);
  }, []);

  const setDefaultAddress = useCallback(async (id: string) => {
    try {
      // Use Next.js API instead of PHP RPC (uses NextAuth session, no token needed)
      const apiResponse = await fetch(`/api/addresses/${id}/set-default`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await apiResponse.json();

      // Check for basic success before validation
      if (data?.success) {
        void trackAccountEvent({
          event_type: 'address_updated',
          section: 'addresses',
          address_id: addressIdForEvent(id),
          is_default_address: true,
        });
        // Force refresh addresses after successful mutation
        setAddressesLoaded(false);
        addressesFetchingRef.current = false; // Reset flag to allow fetch
        await fetchAddressesInternal();

        notify({
          type: 'success',
          msg: 'Default address updated',
        });
      } else {
        notify({
          type: 'error',
          msg: data?.error || 'Failed to set default address',
        });
      }
    } catch (error) {
      trackError(...sessionErrorArgs('setDefaultAddress', error, 'Error setting default address'));
      notify({
        type: 'error',
        msg: thrownMessage(error, 'Error setting default address'),
      });
    }
  }, [fetchAddressesInternal, notify]);

  const deactivateAddress = useCallback(async (id: string) => {
    try {
      // Use Next.js API instead of PHP RPC (uses NextAuth session, no token needed)
      const apiResponse = await fetch(`/api/addresses/${id}/deactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await apiResponse.json();
      const response = DeactivateAddressResponseSchema.parse(data);

      if (response?.success) {
        void trackAccountEvent({
          event_type: 'address_deleted',
          section: 'addresses',
          address_id: addressIdForEvent(id),
        });
        // Force refresh addresses after mutation
        setAddressesLoaded(false);
        addressesFetchingRef.current = false; // Reset flag to allow fetch
        await fetchAddressesInternal();
        notify({
          type: 'success',
          msg: 'Address removed successfully',
        });
        return true;
      } else {
        // The schema is a union on `success`, so this is the refusal. Cast because
        // tsconfig's strict: false keeps TypeScript from narrowing the union.
        const refusal = response as Extract<typeof response, { success: false }>;
        if (refusal.errCode === ADDRESS_CONNECTED_TRANSACTIONS) {
          notify({
            type: 'error',
            msg: 'This address cannot be deleted as it has associated transactions',
          });
          return false;
        } else if (refusal.errCode === ADDRESS_LAST_ADDRESS) {
          notify({
            type: 'error',
            msg: 'Cannot delete your last address',
          });
          return false;
        } else {
          notify({
            type: 'error',
            msg: refusal.error || 'Failed to delete address',
          });
          return false;
        }
      }
    } catch (error) {
      trackError(...sessionErrorArgs('deactivateAddress', error, 'Error deactivating address'));
      notify({
        type: 'error',
        msg: 'Failed to delete address. Please try again.',
      });
      return false;
    }
  }, [fetchAddressesInternal, notify]);

  /**
   * Memoized because consumers depend on its identity, not just its contents.
   *
   * `useSalesPage` builds its fetch callback from this object and feeds it into
   * an effect whose first statement is `setIsLoading(true)`. As a fresh literal
   * every render, that made the Sales page refetch and flash its skeleton on any
   * provider re-render at all — a toast, an address fetch, the `setClientSide`
   * flip on mount (which alone made every load fetch twice). Marking an order
   * delivered fired a notification and wiped the list the seller was looking at.
   *
   * `getProductPath` is hoisted to a module constant for the same reason: an
   * inline arrow is a new function on every render and would defeat the memo.
   */
  const value: SessionContextType = useMemo(() => ({
    clientSide,
    transactions,
    addresses,
    addressesLoaded,
    shippingAddress,
    tempShippingAddress,
    getTransactionsOnLoad,
    getSellerTransactions,
    getShippingLabel,
    getLabelQuote,
    retryShippingLabel,
    getShippingQrCode,
    markTransactionRead,
    getUnreadNotices,
    getAddresses,
    createAddress,
    setShippingAddress,
    setTempShippingAddress,
    setDefaultAddress,
    deactivateAddress,
    setLocation,
    geoLocate,
    getProductPath,
  }), [
    clientSide,
    transactions,
    addresses,
    addressesLoaded,
    shippingAddress,
    tempShippingAddress,
    getTransactionsOnLoad,
    getSellerTransactions,
    getShippingLabel,
    getLabelQuote,
    retryShippingLabel,
    getShippingQrCode,
    markTransactionRead,
    getUnreadNotices,
    getAddresses,
    createAddress,
    setShippingAddress,
    setTempShippingAddress,
    setDefaultAddress,
    deactivateAddress,
    setLocation,
    geoLocate,
  ]);

  return <AppSessionContext.Provider value={value}>{children}</AppSessionContext.Provider>;
};

export default AppSessionProvider;